/**
 * Colourways for the striped cover placeholder.
 *
 * A cover is built around one of four accent families ("Papier de nuit" —
 * see memoneo's `backend/app/products/chouette/cover_guidelines.md`). The
 * placeholder shown while a story has no cover yet now picks a family too,
 * instead of being mint every time: a library mid-generation used to be a
 * wall of identical mint stripes, with nothing to tell one card from the
 * next.
 *
 * Colours are CSS variables, not hexes — `tokens.css` stays the single
 * place where "what colour is sun" is answered.
 *
 * The API tells us which family a card's cover uses (`cover_colorway`, on
 * `StoryCard` and `PackCard`), so `resolveColorway` prefers that and the
 * placeholder matches the cover that is coming. The local hash below is
 * the fallback for anything that has no such field — a backend older than
 * that API change, or an `AudioTrack` assembled client-side.
 *
 * ⚠️ The fallback does NOT reproduce the backend's assignment: there the
 * colorway comes from a blake2b over the slug (`cover._pick_colorway`),
 * here from a small FNV-1a, because pulling blake2b into the bundle to
 * tint a placeholder would be absurd. When it is in play the card can be
 * mint while it generates and lilas once its cover lands. That is bounded
 * — a placeholder and its own cover are never on screen together, the
 * placeholder existing precisely because `cover_svg` is null — and it
 * stops happening entirely once the API field is there.
 */
import type { CSSProperties } from "react";

export interface Colorway {
  key: string;
  accent: string;
  accentDark: string;
}

/** Same order and families as the backend's `_COLORWAYS`. */
export const COLORWAYS: Colorway[] = [
  { key: "sun", accent: "var(--color-sun)", accentDark: "var(--color-sun-dark)" },
  { key: "mint", accent: "var(--color-mint)", accentDark: "var(--color-mint-dark)" },
  { key: "coral", accent: "var(--color-coral)", accentDark: "var(--color-coral-dark)" },
  { key: "lilas", accent: "var(--color-lilas)", accentDark: "var(--color-lilas-dark)" },
];

/** The family named by the API, or `undefined` if it named none we know. */
export function colorwayByKey(key: string | null | undefined): Colorway | undefined {
  return key ? COLORWAYS.find((c) => c.key === key) : undefined;
}

/**
 * Pick a colourway for a story, stably: the same key always yields the same
 * family, so a card doesn't change colour between two renders or two visits.
 *
 * FNV-1a over the key's code units. `Math.imul` because the multiply
 * overflows 32 bits on nearly every character and plain `*` would silently
 * go through float arithmetic, losing the low bits that make the hash mix.
 */
export function colorwayFor(key: string): Colorway {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return COLORWAYS[(h >>> 0) % COLORWAYS.length];
}

/**
 * What the API said, falling back to hashing a stable local key.
 *
 * Unknown names fall back rather than throw: a backend that grows a fifth
 * family should tint this one wrong, not render an untinted — and so
 * invisible — placeholder.
 */
export function resolveColorway(apiKey: string | null | undefined, fallbackKey: string): Colorway {
  return colorwayByKey(apiKey) ?? colorwayFor(fallbackKey);
}

/**
 * The two custom properties `.cover-placeholder` reads (see `index.css`).
 * Exported separately because the generation screen paints the same stripes
 * on a bare div rather than through `<Cover>`.
 */
export function placeholderStyle(apiKey: string | null | undefined, fallbackKey: string): CSSProperties {
  const cw = resolveColorway(apiKey, fallbackKey);
  return { "--ph-a": cw.accent, "--ph-b": cw.accentDark } as CSSProperties;
}
